<template>
    <div class="close-header-title no-con">
        <section class="booking-section">
            <div class="container-inner">
                <div v-if="messageToShow" :class="{ 'error-message': isError, 'success-message': isSuccess }">
                    {{ messageToShow }}
                </div>
                <div class="row">
                    <div class="box-column px-2" v-for="(request, requestIndex) in bookingRequests" :key="'request-' + request.id">
                        <b-card tag="article" class="booking-request-card">
                            <b-carousel
                                v-if="request.property && request.property.photos && request.property.photos.length"
                                :id="'request-carousel-' + request.id"
                                v-model="requestSlide[requestIndex]"
                                :interval="0"
                                :no-hover-pause="true"
                                controls
                                indicators
                                background="#ababab"
                                img-width="1024"
                                img-height="480"
                                style="text-shadow: 1px 1px 2px #333;"
                            >
                                <b-carousel-slide
                                    v-for="(photo, photoIndex) in request.property.photos"
                                    :key="'request-photo-' + request.id + '-' + photoIndex"
                                    :img-src="photo.path"
                                />
                            </b-carousel>
                            <div v-else class="booking-request-no-image">
                                <img src="../public/assets/images/NoBooking.png" :alt="$t('booking.no_bookings_image_alt')" />
                            </div>
                            <div class="box-content">
                                <b-card-text>
                                    <strong>{{ $t('booking.booking_request') }}</strong>
                                </b-card-text>
                                <b-card-text>
                                    <strong>{{ $t('booking.property_type') }}: </strong>
                                    {{ request.property ? request.property.property_type : '-' }}
                                </b-card-text>
                                <b-card-text>
                                    <strong>{{ $t('booking.dates') }}: </strong>
                                    {{ formatDate(request.request_payload && request.request_payload.startDate) }} -
                                    {{ formatDate(request.request_payload && request.request_payload.endDate) }}
                                </b-card-text>
                                <b-card-text class="address-content" v-if="request.property">
                                    <strong>{{ $t('booking.address') }}: </strong>
                                    {{ formatRequestAddress(request.property) }}
                                </b-card-text>
                                <b-card-text v-if="request.request_payload && request.request_payload.totalPrice">
                                    <strong>{{ $t('booking.total_price') }}: </strong>
                                    {{ request.request_payload.paymentCurrency || '' }}
                                    {{ request.request_payload.totalPrice }}
                                </b-card-text>
                                <b-card-text>
                                    <strong>{{ $t('booking.request_status') }}: </strong>
                                    <span :class="requestStatusClass(request)">{{ formatRequestStatus(request) }}</span>
                                </b-card-text>
                                <b-card-text v-if="getRequestEffectiveStatus(request) === 'pending'">
                                    <strong>{{ $t('booking.awaiting_host_response') }}</strong>
                                </b-card-text>
                                <b-card-text v-else-if="getRequestEffectiveStatus(request) === 'rejected'">
                                    <strong>{{ $t('booking.request_rejected_message') }}</strong>
                                </b-card-text>
                                <b-card-text v-else-if="getRequestEffectiveStatus(request) === 'expired'">
                                    <strong>{{ $t('booking.request_expired_message') }}</strong>
                                </b-card-text>
                            </div>
                        </b-card>
                    </div>
                    <div class="box-column px-2" v-for="(booking, index) in bookings" :key="booking.id">
                        <b-card :title="booking.title" img-alt="Image" img-top tag="article">
                            <!-- Property Image Carousel -->
                            <b-carousel
                                :id="'booking-carousel-' + booking.id"
                                v-model="slide[index]"
                                :interval="0"
                                :no-hover-pause="true"
                                controls
                                indicators
                                background="#ababab"
                                img-width="1024"
                                img-height="480"
                                style="text-shadow: 1px 1px 2px #333;"
                            >
                                <b-carousel-slide
                                    v-for="(photo, photoIndex) in booking.property.photos"
                                    :key="'booking-photo-' + booking.id + '-' + photoIndex"
                                    :img-src="photo.path"
                                />
                            </b-carousel>

                            <!-- Property Details -->
                            <div class="box-content">
                                <b-card-text>
                                    <strong>{{ $t('booking.property_type') }}: </strong> {{
                                        booking.property.property_type }}
                                </b-card-text>
                                <b-card-text>
                                    <strong>{{ $t('booking.dates') }}: </strong> {{ formatDate(booking.start_date) }} -
                                    {{
                                        formatDate(booking.end_date) }}
                                </b-card-text>
                                <b-card-text class="address-content">
                                    <strong>{{ $t('booking.address') }}: </strong>
                                    {{ booking.property.city }}, {{ booking.property.state }}, {{
                                        booking.property.country }}
                                </b-card-text>
                                <b-card-text>
                                    <strong>{{ $t('booking.request_tax_invoice') }}: </strong>
                                    <a href="#" @click.prevent="requestTaxInvoice(booking.id, booking.user_id)">{{
                                        $t('booking.click_here') }}</a>
                                </b-card-text>

                                <!-- Cancellation Policy -->
                                <b-card-text class="cancellation-info"
                                    v-if="booking.cancellationInfo.status === 'active'">
                                    <strong>{{ $t('booking.cancellation_policy') }}:</strong>
                                    <ul>
                                        <li v-if="booking.cancellationInfo.fullrefund">
                                            <strong>{{ $t('booking.full_refund') }}:</strong>{{
                                                $t('booking.cancel_before', {
                                                    date: booking.cancellationInfo.fullRefundDate
                                                }) }}
                                        </li>
                                        <li v-if="booking.cancellationInfo.within30Days">
                                            <strong>{{ $t('booking.refund_depends_reason') }}:</strong> {{
                                                $t('booking.cancel_within_30_days') }}
                                        </li>
                                        <li v-if="booking.cancellationInfo.within24h">
                                            <strong>{{ $t('booking.within_24_hours') }}:</strong>
                                            {{ $t('booking.property_problem_full_refund') }}
                                        </li>
                                    </ul>
                                </b-card-text>

                                <!-- Countdown or Message -->
                                <b-card-text v-if="booking.countdownEnd">
                                    <strong>{{ $t('booking.check_in_countdown') }}:</strong> {{
                                        getCountdown(booking.countdownEnd) }}
                                </b-card-text>
                                <b-card-text v-else>
                                    <strong>{{ booking.cancellationInfo.message }}</strong>
                                </b-card-text>

                                <b-card-text v-if="booking.security_deposit">
                                    <strong>{{ $t('payment.security_deposit') }}:</strong>
                                    {{ booking.payment_currency || '' }} {{ booking.security_deposit.amount }}
                                </b-card-text>
                                <b-card-text v-if="booking.security_deposit">
                                    <strong>{{ $t('payment.security_deposit_status') }}:</strong>
                                    {{ formatDepositStatus(booking.security_deposit.status) }}
                                </b-card-text>
                                <b-card-text v-if="showDepositReleaseNote(booking)" class="deposit-release-note">
                                    {{ $t('payment.deposit_mutual_release_note') }}
                                    {{ $t('payment.deposit_auto_refund_note') }}
                                </b-card-text>

                                <!-- Action Buttons -->
                                <div class="d-flex box-footer flex-wrap" style="gap: 6px;">
                                    <div class="gap-y-xs">
                                        <a class="card-blue-btn" type="button" data-cy="card-view-btn"
                                            @click="propertyDetail(booking.id)">{{ $t('booking.view_detail') }}</a>
                                    </div>
                                    <div class="gap-y-xs"
                                        v-if="canGuestConfirmDeposit(booking)">
                                        <button class="card-blue-btn" type="button"
                                            :disabled="booking.isConfirmingDeposit"
                                            @click="confirmDepositRelease(booking)">
                                            {{ booking.isConfirmingDeposit ? $t('payment.processing') : $t('payment.confirm_deposit_release') }}
                                        </button>
                                    </div>
                                    <div class="gap-y-xs"
                                        v-if="booking.cancellationInfo.status !== 'expired' &&
                                            (booking.cancellationInfo.fullrefund || booking.cancellationInfo.within30Days || booking.cancellationInfo.within24h)">
                                        <button class="card-blue-btn" type="button" data-cy="card-view-btn"
                                            :disabled="booking.isCancelling"
                                            @click="cancelbooking(booking.id, booking)">
                                            {{ booking.isCancelling ? $t('booking.cancelling') :
                                                $t('booking.cancel_booking') }}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </b-card>
                    </div>

                    <!-- No Bookings -->
                    <div class="" v-if="bookings.length === 0 && bookingRequests.length === 0">
                        <div class="card-box">
                            <img src="../public/assets/images/NoBooking.png" :alt="$t('booking.no_bookings_image_alt')" />
                            <p>{{ $t('booking.no_bookings_available') }}</p>
                            <a href="/" class="back-btn">{{ $t('booking.back_to_home') }}</a>
                        </div>
                    </div>
                </div>

                <!-- Cancel Reason Modal -->
                <b-modal id="cancelBookingModal" ref="cancelBookingModal" hide-footer
                    :title="$t('booking.reason_for_cancellation')">
                    <b-form>
                        <b-form-group :label="$t('booking.select_reason_for_cancellation')" class="cancelBookingModal">
                            <b-form-radio-group v-model="selectedReason"
                                :options="cancellationReasons"></b-form-radio-group>
                        </b-form-group>

                        <div class="d-flex justify-content-end">
                            <b-button class="mr-2" style="border-radius: 10px !important;" variant="danger"
                                @click="$bvModal.hide('cancelBookingModal')">{{ $t('sweetalert.cancel') }}</b-button>
                            <b-button variant="primary" class="ml-2" @click="submitCancelBooking"
                                :disabled="isSubmitting">{{ $t('common.submit') }}</b-button>
                        </div>
                    </b-form>
                </b-modal>
            </div>
        </section>
    </div>
</template>

<script>
import API from '@/api';
import axios from 'axios';
import Vue from 'vue';
import moment from 'moment';
import Swal from 'sweetalert2';

import { Loader } from '@googlemaps/js-api-loader';
export default {
    name: 'my_bookings',
    components: {
    },
    data() {
        return {
            showbooking: false, // Controls conditional rendering of booking cards
            isSubmitting: false,
            isCancelling: false,
            selectedBooking: null,
            bookings: [], // Array to store fetched booking data
            bookingRequests: [],
            requestSlide: [],
            slide: [],
            messageToShow: '',
            isError: false,
            isSuccess: false,
            moment,
            selectedReason: null,
            selectedLanguage: null,
            countdownTimer: null,
            cancellationReasons: [
                { value: 'property_problem', text: this.$t('booking.cancel_property_problem') },
                { value: 'personal_choice', text: this.$t('booking.cancel_personal_choice') }
            ],
        };
    },
    mounted() {
        this.getbookings();

        this.countdownTimer = setInterval(() => {
            this.$forceUpdate();
        }, 1000);
        // Vue.prototype.$EventBus.$on('changeLanguage', this.onLanguageChanged);
        Vue.prototype.$EventBus && Vue.prototype.$EventBus.$on('changeLanguage', this.onLanguageChanged);
    },
    beforeDestroy() {
        clearInterval(this.countdownTimer);
        Vue.prototype.$EventBus && Vue.prototype.$EventBus.$off('changeLanguage', this.onLanguageChanged);
    },
    watch: {
        '$i18n.locale': function (newLocale, oldLocale) {
            this.getbookings();
            this.$forceUpdate();
        }
    },
    methods: {
        formatDate(date) {
            if (!date) return '-';
            return moment(date).format('DD-MM-YYYY');
        },
        formatDepositStatus(status) {
            if (!status) return '-';
            return status.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
        },
        showDepositReleaseNote(booking) {
            const deposit = booking.security_deposit;
            if (!deposit) return false;
            const checkInOut = booking.guest_check_in_out || {};
            return checkInOut.is_checked_out === 1 || checkInOut.is_checked_out === true;
        },
        canGuestConfirmDeposit(booking) {
            const deposit = booking.security_deposit;
            if (!deposit || deposit.guest_confirmed_at) return false;
            if (!['held', 'awaiting_release'].includes(deposit.status)) return false;
            const checkInOut = booking.guest_check_in_out || {};
            return checkInOut.is_checked_out === 1 || checkInOut.is_checked_out === true;
        },
        confirmDepositRelease(booking) {
            this.$set(booking, 'isConfirmingDeposit', true);
            API.guestConfirmSecurityDeposit(booking.id, (data) => {
                this.$set(booking, 'isConfirmingDeposit', false);
                if (data.status === 200) {
                    Swal.fire({
                        icon: 'success',
                        title: this.$t('payment.deposit_release_confirmed'),
                        confirmButtonText: this.$t('sweetalert.OK'),
                    });
                    this.getbookings();
                } else {
                    Swal.fire({
                        icon: 'error',
                        title: data.message || this.$t('alerts.error'),
                        confirmButtonText: this.$t('sweetalert.OK'),
                    });
                }
            }, () => {
                this.$set(booking, 'isConfirmingDeposit', false);
            });
        },
        getRequestEffectiveStatus(request) {
            if (!request) return '';
            if (request.status !== 'pending') return request.status;
            if (request.expires_at && moment().isAfter(moment(request.expires_at))) return 'expired';
            return 'pending';
        },
        formatRequestStatus(request) {
            const status = this.getRequestEffectiveStatus(request);
            if (!status) return '-';
            return status.charAt(0).toUpperCase() + status.slice(1);
        },
        formatRequestAddress(property) {
            if (!property) return '-';
            const parts = [property.city, property.state, property.country].filter(Boolean);
            if (parts.length) return parts.join(', ');
            return property.address || '-';
        },
        requestStatusClass(request) {
            const status = this.getRequestEffectiveStatus(request);
            return `request-status request-status--${status}`;
        },
        onLanguageChanged(language) {
            if (language === 'en') {
                this.$i18n.locale = 'en';
            } else {
                this.$i18n.locale = language;
            }
            this.selectedLanguage = language;
            this.getbookings();
        },
        getCountdown(endTime) {
            const now = moment();
            const end = moment(endTime);
            const duration = moment.duration(end.diff(now));

            if (duration.asMilliseconds() <= 0) return this.$t('booking.expired');

            const hours = String(Math.floor(duration.asHours())).padStart(2, '0');
            const minutes = String(duration.minutes()).padStart(2, '0');
            const seconds = String(duration.seconds()).padStart(2, '0');

            return `${hours}:${minutes}:${seconds}`;
        },
        changeLanguage(language) {
            Vue.prototype.$EventBus && Vue.prototype.$EventBus.$emit('changeLanguage', language);
            this.onLanguageChanged(language);
        },
        cancelbooking(id, booking) {
            const cancellation = booking.cancellationInfo;

            // Case 1: ≥30 days before check-in → full refund (no reason needed)
            if (cancellation.fullrefund) {
                Swal.fire({
                    icon: 'warning',
                    type: 'warning',
                    title: this.$t('booking.cancel_confirm_title'),
                    text: this.$t('booking.full_refund_message', { date: cancellation.fullRefundDate }),
                    showCancelButton: true,
                    confirmButtonColor: '#d33',
                    cancelButtonColor: '#3085d6',
                    confirmButtonText: this.$t('booking.yes_cancel'),
                    cancelButtonText: this.$t('booking.no_keep'),
                }).then((result) => {
                    if (result.value) {
                        this.$set(booking, 'isCancelling', true);
                        this.proceedWithCancellation(id, booking);
                    }
                });
                return;
            }

            // Case 2: <30 days OR within 24h after check-in → reason required
            if (cancellation.within30Days || cancellation.within24h) {
                this.selectedBooking = booking;
                this.selectedReason = null;
                this.$bvModal.show('cancelBookingModal');
                return;
            }

            // Case 3: >24h after check-in OR booking started → not allowed
            if (cancellation.status === 'expired') {
                Swal.fire({
                    icon: 'error',
                    type: 'error',
                    title: this.$t('booking.cancellation_not_allowed'),
                    text: cancellation.message,
                    confirmButtonColor: '#d33',
                    confirmButtonText: this.$t('sweetalert.OK'),
                });
                return;
            }
        },
        // Full refund without reason
        proceedWithCancellation(id, booking) {
            API.cancelbooking({ id: id }, (data) => {
                this.$set(booking, 'isCancelling', false);
                if (data.status === 200) {
                    Swal.fire({
                        icon: 'success',
                        type: 'success',
                        title: this.$t('booking.booking_canceled'),
                        text: this.$t('invoice.full_refund_recorded'),
                        // text: data.message,
                        confirmButtonColor: '#3085d6',
                        confirmButtonText: this.$t('sweetalert.OK')
                    });
                    this.getbookings(); // Refresh bookings
                } else {
                    Swal.fire({
                        icon: 'error',
                        type: 'error',
                        title: this.$t('alerts.error'),
                        text: data.message || this.$t('alerts.error'),
                        confirmButtonColor: '#d33',
                        confirmButtonText: this.$t('sweetalert.OK')
                    });
                }
            }, (err) => {
                this.$set(booking, 'isCancelling', false);
                console.error(err);
            });
        },

        // Cancellation that requires a reason
        submitCancelBooking() {
            const reason = this.selectedReason;
            const booking = this.selectedBooking;

            if (!reason) {
                Swal.fire({
                    icon: 'error',
                    type: 'error',
                    title: this.$t('booking.no_reason_selected'),
                    text: this.$t('booking.select_reason_message'),
                    confirmButtonColor: '#d33',
                    confirmButtonText: this.$t('sweetalert.OK')
                });
                return;
            }

            this.isSubmitting = true;
            this.$set(booking, 'isCancelling', true);
            API.cancelbooking({ id: booking.id, reason: reason }, (data) => {
                this.isSubmitting = false;
                this.$set(booking, 'isCancelling', false);
                if (data.status === 200) {
                    this.$bvModal.hide('cancelBookingModal');
                    const refundText = reason === 'property_problem' ? this.$t('booking.full_refund') : this.$t('booking.partial_refund');
                    Swal.fire({
                        icon: 'success',
                        type: 'success',
                        title: this.$t('booking.booking_canceled'),
                        text: this.$t('booking.canceled_reason_message', { reason: refundText }),
                        confirmButtonColor: '#3085d6',
                        confirmButtonText: this.$t('sweetalert.OK')
                    });
                    this.getbookings();
                } else {
                    Swal.fire({
                        icon: 'error',
                        type: 'error',
                        title: this.$t('alerts.error'),
                        text: data.message || this.$t('alerts.error'),
                        confirmButtonColor: '#d33',
                        confirmButtonText: this.$t('sweetalert.OK')
                    });
                }
            }, (err) => {
                this.isSubmitting = false;
                this.$set(booking, 'isCancelling', false);
                console.error(err);
            });
        },
        handleApiError(error) {
            console.error(error);
            this.displayMessage(error, true);
        },
        displayMessage(message, isError = false) {
            this.messageToShow = message;
            this.isError = isError;
            this.isSuccess = !isError;
            this.clearMessageAfterDelay();
        },
        clearMessageAfterDelay() {
            this.getbookings();
            setTimeout(() => {
                this.messageToShow = '';
                this.isError = false;
                this.isSuccess = false;
            }, 5000);
        },
        propertyDetail(id) {
            let Authorization = localStorage.getItem('isLoggedin');
            if (Authorization) {
                this.$router.push({ name: 'invoice_detail', params: { id: id } });

            } else {
                this.$router.push({ name: 'guest_login' });
            }
        },
        prefund(startDate) {
            const futureDate = new Date(startDate);
            futureDate.setDate(futureDate.getDate() - 8);
            return futureDate.toLocaleDateString('en-US', { month: 'short', day: '2-digit' });
        },
        crefund(startDate) {
            const futureDate = new Date(startDate);
            futureDate.setDate(futureDate.getDate() - 15);
            return futureDate.toLocaleDateString('en-US', { month: 'short', day: '2-digit' });
        },
        requestTaxInvoice(id) {
            API.addTaxRequest({ id: id },
                data => {
                    if (data.status === 'exists') {
                        Swal.fire({
                            icon: 'warning',
                            type: 'warning',
                            title: this.$t('booking.already_requested'),
                            // text: data.message,
                            text: this.$t('invoice.tax_invoice_exists'),
                            confirmButtonText: this.$t('sweetalert.OK')
                        });
                        this.$set(this.requestedInvoices, id, true);
                    } else if (data.status === 'created') {
                        Swal.fire({
                            icon: 'success',
                            type: 'success',
                            title: this.$t('booking.request_sent'),
                            text: this.$t('invoice.tax_invoice_success'),
                            // text: data.message,
                            confirmButtonText: this.$t('sweetalert.OK')
                        });
                        this.$set(this.requestedInvoices, id, true);
                    } else if (data.error) {
                        this.handleApiError(data.error);
                    }
                },
                err => {
                    console.error(err);
                    // this.handleApiError("Something went wrong. Please try again.");
                }
            );
        },
        loadBookingRequests() {
            API.getBookingRequests({ role: 'guest', page: 1, perPage: 100 }, (data) => {
                this.bookingRequests = (data.data || []).filter((request) => request.status !== 'accepted');
                this.requestSlide = this.bookingRequests.map(() => 0);
            }, () => {
                this.bookingRequests = [];
                this.requestSlide = [];
            });
        },
        getbookings() {
            this.loadBookingRequests();
            API.getusersbooking(data => {
                this.bookings = data.data.booking.map(booking => {
                    const startMoment = moment(booking.start_date, 'YYYY-MM-DD');
                    const today = moment();
                    const checkInOut = booking.guest_check_in_out || {};
                    const isCheckedIn = checkInOut.is_checked_in === 1;
                    const checkInTime = checkInOut.check_in_date_time ? moment(checkInOut.check_in_date_time) : null;

                    const daysUntilCheckIn = startMoment.diff(today, 'days');
                    const fullRefundCutoff = startMoment.clone().subtract(30, 'days');

                    let cancellationInfo = {
                        status: 'active',
                        fullrefund: false,
                        within30Days: false,   // renamed (was noRefund)
                        within24h: false,      // renamed (was parrefund)
                        fullRefundDate: fullRefundCutoff.format('DD-MM-YYYY'),
                        message: ''
                    };

                    let countdownEnd = null;

                    if (isCheckedIn && checkInTime) {
                        countdownEnd = checkInTime.clone().add(24, 'hours');

                        if (today.isBefore(countdownEnd)) {
                            cancellationInfo.within24h = true;
                            cancellationInfo.message = this.$t('booking.within_24h_message');
                        } else {
                            cancellationInfo.status = 'expired';
                            cancellationInfo.message = this.$t('booking.grace_period_expired');
                        }

                    } else if (startMoment.isSameOrBefore(today, 'day')) {
                        // Booking date is today or earlier
                        cancellationInfo.status = 'expired';
                        cancellationInfo.message = this.$t('booking.booking_started');
                    } else if (daysUntilCheckIn >= 30) {
                        cancellationInfo.fullrefund = true;
                        cancellationInfo.message = this.$t('booking.full_refund_eligible');
                    } else {
                        cancellationInfo.within30Days = true;
                        cancellationInfo.message = this.$t('booking.within_30_days_message');
                    }

                    return {
                        ...booking,
                        is_checked_in: isCheckedIn,
                        countdownEnd,
                        cancellationInfo,
                        isCancelling: false
                    };
                });
                this.slide = this.bookings.map(() => 0);
            });
        },
    },
    computed: {
    },
    created() {
    },
}
</script>
<style>
.box-footer .card-blue-btn {
    background-color: #70d3ff;
    color: #fff;
    font-weight: 500;
    font-size: 16px;
    padding-left: 12px;
    padding-right: 12px;
    gap: 8px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    border-radius: 10px;
    text-decoration: none;
}
.box-footer button.card-blue-btn {
    border: 1px solid #70d3ff;
}

.error-message {
    background-color: #f8d7da;
    color: #721c24;
    padding: 10px;
    margin-bottom: 10px;
    border: 1px solid #f5c6cb;
}

.card-box {
    background-color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    border-radius: 10px;
    padding: 30px 20px;
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    flex-direction: column;
    min-width: 300px;
}

.card-box p {
    text-transform: uppercase;
    font-size: 20px;
    font-weight: 500;
    margin-bottom: 0;
    margin-top: 20px;
    text-align: center;
}

.card-box .back-btn {
    background-color: #70d3ff;
    color: white;
    border: none;
    border: 1px solid #70d3ff;
    padding: 10px 20px;
    font-size: 16px;
    border-radius: 60px;
    cursor: pointer;
    width: max-content;
    transition: background-color 0.3s ease;
    font-weight: 500;
    text-decoration: none;
}

.card-box .back-btn:hover {
    background-color: #fff;
    color: #70d3ff;
}

.success-message {
    background-color: #d4edda;
    color: #155724;
    padding: 10px;
    margin-bottom: 10px;
    border: 1px solid #c3e6cb;
}

.booking-section .row {
    margin: 0 -0.5rem;
}

.booking-section .card {
    padding: 0px !important;
    border-radius: 12px;
    overflow: hidden;
    position: relative;
    border: 1px solid #e7e7e8;
    margin: 0;
    margin-bottom: 0;
}

.booking-section .card .card-body {
    padding: 0;
}

.booking-section .card .card-body .box-content {
    padding: 12px;
    height: auto;
}

.booking-section .card .card-body .box-content .address-content {
    min-height: auto;
}

.booking-section .card .card-body .box-content .box-footer {
    display: flex;
    justify-content: space-between;
}

.booking-request-no-image {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 180px;
    background: #f5f5f5;
    padding: 20px;
}

.booking-request-no-image img {
    max-width: 120px;
    opacity: 0.6;
}

.request-status {
    font-weight: 600;
}

.request-status--pending {
    color: #e6a700;
}

.request-status--rejected {
    color: #c0392b;
}

.request-status--expired {
    color: #6c757d;
}

.request-status--cancelled {
    color: #6c757d;
}

.box-column {
    width: 16.6%;
    margin-bottom: 24px;
}

.custom-swal-popup button {
    border-radius: 10px !important;
}

.col-form-label {
    font-weight: 500;
}

@media only screen and (max-width: 1719px) {
    .box-column {
        width: 20%;
    }
}

@media only screen and (max-width: 1399px) {
    .box-column {
        width: 25%;
    }
}

@media only screen and (max-width: 1149px) {
    .box-column {
        width: 33%;
    }
}

@media only screen and (max-width: 767px) {
    .box-column {
        width: 50%;
    }

    .client-dashbboard .main-panel .content-wrapper {
        padding: 25px 20px;
    }
}

@media only screen and (max-width: 574px) {
    .box-column {
        width: 80%;
        margin-left: auto;
        margin-right: auto;
    }
}

@media only screen and (max-width: 400px) {
    .box-column {
        width: 90%;
        margin-left: auto;
        margin-right: auto;
    }
}
</style>